import { Badge } from "@/components/ui/badge.tsx";
import { Input } from "@/components/ui/input.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebounce } from "@/lib/hooks/use-debounce.ts";
import { cn } from "@/lib/utils.ts";
import { Label } from "@radix-ui/react-label";
import { useRouter, useSearch } from "@tanstack/react-router";
import { SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";

const FILTERS = [
  {
    label: "All",
    value: "all",
  },
  {
    label: "Male",
    value: "male",
  },
  {
    label: "Female",
    value: "female",
  },
  {
    label: "Other",
    value: "other",
  },
];

const MENTOR_MATCH_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Mentor Matched", value: "true" },
  { label: "No Mentor Match", value: "false" },
];

const ONBOARDING_STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Completed", value: "true" },
  { label: "Incomplete", value: "false" },
];

export function Filters() {
  const { per_page, search: urlSearch } = useSearch({
    strict: false,
  }) as Record<string, string>;

  const router = useRouter();
  const [search, setSearch] = useState("");
  const [customFilter, setCustomFilter] =
    useState<(typeof FILTERS)[number]["value"]>("all");
  const [mentorMatched, setMentorMatched] = useState("all");
  const [onboardingCompleted, setOnboardingCompleted] = useState("all");

  const debouncedSearch = useDebounce(search, 500);

  const updateFilters = (newFilters: Record<string, any>) => {
    router.navigate({
      search: {
        search: debouncedSearch,
        page: 1,
        per_page,
        customFilter,
        mentorMatched: mentorMatched === "all" ? undefined : mentorMatched,
        onboardingCompleted:
          onboardingCompleted === "all" ? undefined : onboardingCompleted,
        ...newFilters,
      } as any,
    });
  };

  useEffect(() => {
    updateFilters({});
  }, [debouncedSearch, customFilter, mentorMatched, onboardingCompleted]);

  useEffect(() => {
    if (urlSearch) {
      setSearch(urlSearch);
    }
  }, [urlSearch]);

  return (
    <div className="mb-2 flex flex-col gap-2 p-2">
      <div className="flex items-end gap-2">
        <div className="flex flex-1 items-center gap-2">
          {FILTERS.map((filter) => (
            <Badge
              key={filter.value}
              variant={customFilter === filter.value ? "secondary" : "outline"}
              className={cn("cursor-pointer px-4 py-2 capitalize", {
                "bg-black text-white hover:bg-black":
                  customFilter === filter.value,
              })}
              onClick={() => {
                setCustomFilter(filter.value);
              }}
            >
              {filter.label}
            </Badge>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium text-muted-foreground">
              Mentor Match Status
            </Label>
            <Select
              value={mentorMatched}
              onValueChange={(value) => setMentorMatched(value)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Mentor Match Status" />
              </SelectTrigger>
              <SelectContent>
                {MENTOR_MATCH_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium text-muted-foreground">
              Onboarding Status
            </Label>
            <Select
              value={onboardingCompleted}
              onValueChange={(value) => setOnboardingCompleted(value)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Onboarding Status" />
              </SelectTrigger>
              <SelectContent>
                {ONBOARDING_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Input
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startIcon={<SearchIcon className="h-5 w-5" />}
            className="w-72s w-[300px] rounded-md"
          />
        </div>
      </div>
    </div>
  );
}
